import { useContext, useEffect } from 'react';

import { getIncodeVerificationStatus } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { authorizationHeader } from '../../vehicleCollection/services/apiClient';
import { INCODE_POLLING_STATUS } from '../DocumentCapture.helpers';

type PollingOptions = {
  enquiryId: number;
  intervalMs?: number; // Interval between calls (default: 500ms)
  maxAttempts?: number; // Maximum number of attempts (default: 6)
  onError: (error: any) => void; // Callback when polling fails
  onSuccess: (data: any) => void; // Callback when polling succeeds, returns true to stop polling
};

export const useGetVerificationStatus = ({
  enquiryId,
  intervalMs = 5000,
  maxAttempts = 6,
  onError,
  onSuccess,
}: PollingOptions) => {
  const { userState: user } = useContext(UserContext);
  const authToken = user?.auth_token;

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const attemptPoll = () => {
      if (!enquiryId || !authToken) {
        onError(new Error('Missing required params'));
        return;
      }
      if (cancelled || attempts >= maxAttempts) {
        return;
      }

      getIncodeVerificationStatus(enquiryId, authorizationHeader(authToken), {}, abortController)
        .then((response) => {
          attempts += 1;

          const { verificationStatus: status } = response ?? {};

          if (status === INCODE_POLLING_STATUS.COMPLETED) {
            onSuccess(response);
          } else if (status === INCODE_POLLING_STATUS.FAILED) {
            onError(new Error('Verification failed'));
          } else if (attempts < maxAttempts) {
            timeout = setTimeout(attemptPoll, intervalMs);
          } else {
            onError(new Error('Max attempts reached'));
          }
        })
        .catch((err) => {
          if (!cancelled) {
            onError(err);
          }
        });
    };

    attemptPoll();

    return () => {
      cancelled = true;
      if (timeout) {
        clearTimeout(timeout);
      }
      abortController.abort();
    };
  }, [enquiryId, intervalMs, maxAttempts, onError, onSuccess, authToken]);
};
